import { createMmWsAction } from '@skynet/data-ws-mm';
import type { FormStrikePrice, StrikePrice } from './types';
import { StrikePricesCommands } from './types';
import { ValidStrikeTypes } from './constants';

export type SetFormStrikePricesPayload = FormStrikePrice[];

type AddStrikePricePayload = Omit<StrikePrice, 'id'> & { loanID?: string };

export const addStrikePrice = createMmWsAction<AddStrikePricePayload, AddStrikePricePayload>(
  'loans/addStrikePrice',
  (data) => ({
    command: StrikePricesCommands.AddStrikePrice,
    ...data,
  }),
);

const isFormStrikePrice = (p: unknown): p is FormStrikePrice => {
  if (p == null || typeof p !== 'object') return false;
  // check tokensAmount presence and that strikeDate is not a string (Dayjs expected)
  if (!Object.prototype.hasOwnProperty.call(p, 'tokensAmount')) return false;
  const sd = (p as { strikeDate?: unknown }).strikeDate;
  return typeof sd !== 'string';
};

const toStrikeType = (v: unknown): StrikePrice['strikeType'] | undefined => {
  if (typeof v !== 'number') return undefined;
  return ValidStrikeTypes.find((t) => t === v);
};

export function mapFormStrikePrice(
  formStrikePrice: FormStrikePrice | Omit<StrikePrice, 'id'>,
): Omit<StrikePrice, 'id'> {
  if (isFormStrikePrice(formStrikePrice)) {
    // form object from UI: tokensAmount is string, strikeDate/endDate are Dayjs
    const { tokensAmount, strikePrice, strikeDate, endDate, strikeType, note } = formStrikePrice;

    const normalizedStrikeType = toStrikeType(strikeType);

    const result: Omit<StrikePrice, 'id'> = {
      tokensAmount: Number(tokensAmount ?? 0),
      strikePrice: Number(strikePrice ?? 0),
      strikeDate: strikeDate?.format('YYYY-MM-DD') ?? '',
      endDate: endDate?.format('YYYY-MM-DD') ?? '',
      strikeType: normalizedStrikeType,
      note: note ?? '',
    };

    return result;
  }

  // already JSON-like
  const { tokensAmount, strikePrice, strikeDate, endDate, strikeType, note } = formStrikePrice as
    | Omit<StrikePrice, 'id'>
    | Record<string, unknown>;

  const normalizedStrikeType = toStrikeType(strikeType);

  const result: Omit<StrikePrice, 'id'> = {
    tokensAmount: Number(tokensAmount ?? 0),
    strikePrice: Number(strikePrice ?? 0),
    strikeDate: typeof strikeDate === 'string' ? strikeDate : '',
    endDate: typeof endDate === 'string' ? endDate : '',
    strikeType: normalizedStrikeType,
    note: (typeof note === 'string' ? note : '') ?? '',
  };

  return result;
}

type EditStrikePricePayload = FormStrikePrice & { id: number };

export const editStrikePrice = createMmWsAction<EditStrikePricePayload, StrikePrice>(
  'loans/editStrikePrice',
  (data) => {
    const { id, ...rest } = data;
    const strikePrice = mapFormStrikePrice(rest);
    return {
      command: StrikePricesCommands.EditStrikePrice,
      id,
      ...strikePrice,
    };
  },
);

export const removeStrikePrice = createMmWsAction<number, { id: number }>(
  'loans/removeStrikePrice',
  (id) => ({
    command: StrikePricesCommands.RemoveStrikePrice,
    id,
  }),
);
